import React, { useCallback, useEffect, useRef, useState } from "react"
import { SecondaryBlockFieldsFragment } from "../../../../graphql-types"
import * as styles from "./secondary-blocks.module.scss"
import { useIsMobile } from "../../../hooks"
import SecondaryBlock from "./secondary-block"
import { ArrowLeft, ArrowRight } from "../../layout/icons"

type RenderProps = {
  blocks: SecondaryBlockFieldsFragment[]
  label: string
  previousLabel: string
  nextLabel: string
  locale: string
  defaultLocale: string
}

const SecondaryBlocks: React.FC<RenderProps> = ({ blocks, label, previousLabel, nextLabel, locale, defaultLocale }) => {
  const isMobile = useIsMobile()
  const [isActive, setIsActive] = useState(0)
  const containerRef = useRef<HTMLUListElement>(null)

  const previousButtonLabel = previousLabel
  const nextButtonLabel = nextLabel

  const getItemWidth = () => {
    const firstItem = containerRef.current?.querySelector<HTMLElement>(`.${styles.secondaryBlock}`)
    if (!firstItem) return 0
    const computedGap = parseInt(window.getComputedStyle(containerRef.current!).gap || "12", 10) || 12
    return firstItem.clientWidth + computedGap
  }

  const scrollByItem = (direction: number) => {
    const container = containerRef.current
    const itemWidth = getItemWidth()
    if (!container || !itemWidth) return

    const nextScrollLeft = Math.max(
      0,
      Math.min(container.scrollLeft + direction * itemWidth, container.scrollWidth - container.clientWidth)
    )
    container.scrollTo({ left: nextScrollLeft, top: 0, behavior: "smooth" })
  }

  const scrollToNextItem = () => scrollByItem(1)

  const scrollToPreviousItem = () => scrollByItem(-1)

  const handleScrollAnimation = useCallback(() => {
    if (!isMobile || !containerRef.current) return

    const allBlocks = Array.from(containerRef.current.getElementsByClassName(styles.secondaryBlock))
    allBlocks.forEach((block, i) => {
      if (block.getBoundingClientRect().left < 10) {
        setIsActive(i + 1)
      }
    })
  }, [isMobile])

  useEffect(() => {
    if (isMobile) {
      handleScrollAnimation()
    }
  }, [isMobile, handleScrollAnimation])

  return (
    <div className={styles.secondaryBlocksWrapper}>
      {isMobile && (
        <div className={`${styles.controls} ${styles.controlsPrev}`}>
          <button
            type="button"
            aria-label={previousButtonLabel}
            title={previousButtonLabel}
            onClick={scrollToPreviousItem}
            className={styles.navButton}
          >
            <ArrowLeft />
          </button>
        </div>
      )}

      <ul ref={containerRef} className={styles.secondaryBlocks} aria-label={label} onScroll={handleScrollAnimation}>
        {blocks.map((block, i) => (
          <li className={styles.secondaryBlock} key={block.id}>
            <SecondaryBlock
              block={block}
              isActive={isMobile && isActive === i}
              locale={locale}
              defaultLocale={defaultLocale}
            />
          </li>
        ))}
      </ul>

      {isMobile && (
        <div className={`${styles.controls} ${styles.controlsNext}`}>
          <button
            type="button"
            aria-label={nextButtonLabel}
            title={nextButtonLabel}
            onClick={scrollToNextItem}
            className={styles.navButton}
          >
            <ArrowRight />
          </button>
        </div>
      )}
    </div>
  )
}

export default SecondaryBlocks
